import React, { useEffect } from "react";
import { Button, Card, Typography, Divider } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

const PrivacyPolicies = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem("access_token");

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isCheck = searchParams.get("consent");

  const HandleNav = () => {
    if (token) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    const hideOnPaths = ["/privacypolicy", "/termsandconditions"];
    const isHidden = hideOnPaths.includes(location.pathname);

    const interval = setInterval(() => {
      const tawkIframes = document.querySelectorAll("iframe[src*='tawk.to']");
      const tawkWidget = document.querySelector(".widget-visible");

      if (tawkIframes.length > 0 || tawkWidget) {
        // Stop polling once the elements are found
        clearInterval(interval);

        // Hide all Tawk iframes
        tawkIframes.forEach((iframe) => {
          iframe.setAttribute("style", isHidden && isCheck ? "display: none !important" : "display: block !important");
        });

        // Hide the container if found
        if (tawkWidget) {
          tawkWidget.setAttribute("style", isHidden && isCheck ? "display: none !important" : "display: block !important");
        }
      }
    }, 300); // Check every 300ms

    // Optional cleanup
    return () => clearInterval(interval);
  }, [location.pathname, isCheck]);

  return (
    <div className="pad-privasy" style={{ padding: "50px" }}>
      {!isCheck && (
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => HandleNav()}
          style={{
            fontSize: "15px",

            position: "absolute",
            top: "16px",
            left: "16px",
            zIndex: "99999",
          }}
        >
          Go Back
        </Button>
      )}

      <Card style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }} variant="borderless">
        <Typography>
          <Title level={1}>Privacy Policy</Title>
          <Paragraph>Welcome to Project W.</Paragraph>
          <Paragraph>
            We at Project W (referred to as “we” “us” and “our”) care about your privacy and the protection of your personal data. We have drafted this Privacy Policy to inform you
            about our practices regarding your personal data. This document explains to you what personal data of yours we process, for what purpose we process it and what measures
            we take to ensure that your personal data is secure with us.
          </Paragraph>
          <Paragraph>By using our application, you give consent that you understand and agree to the terms laid down in this privacy policy.</Paragraph>
          <Paragraph>Project W is wholly owned by Area X LLC, registered in the United Arab Emirates.</Paragraph>
          <Paragraph>
            Our website address is <a href="https://projectw.ai">https://projectw.ai</a>.
          </Paragraph>

          <Divider />

          <Title level={2}>Definitions</Title>
          <Paragraph>All capitalized terms seen below shall have the meaning as given in this section:</Paragraph>
          <ul>
            <li>
              <strong>PERSONAL DATA:</strong> This means any information relating to an identified or identifiable individual, to the extent that such information is protected as
              personal data under the Applicable Data Protection Law.
            </li>
            <li>
              <strong>DATA CONTROLLER:</strong> Means the entity which determines the purposes and means of the processing of Personal Data.
            </li>
            <li>
              <strong>DATA PROCESSOR:</strong> Means the entity which Processes Personal Data on behalf of the Data Controller.
            </li>
            <li>
              <strong>DATA SUBJECT:</strong> Means an identified or identifiable individual, who can be identified, directly or indirectly, in particular by reference to an
              identifier such as a name, an ID number, location data, an online ID, or to one or more factors specific to the physical, physiological, genetic, mental, economic,
              cultural, or social identity of that natural person.
            </li>
            <li>
              <strong>PERSONAL DATA BREACH:</strong> Means any unauthorized or unlawful breach of security leading to, or reasonably believed to, the unauthorized or accidental
              destruction, loss, alteration, unauthorized disclosure of or access to Personal Data.
            </li>
            <li>
              <strong>PROCESS OR PROCESSING:</strong> Means any operation or set of operations which is performed on Personal Data or on sets of Personal Data, whether by automated
              means, such as collection, recording, organization, structuring, storage, adaptation or alteration, retrieval, consultation, use, disclosure by transmission,
              dissemination or otherwise making available, alignment or combination, restriction, erasure, or destruction.
            </li>
          </ul>

          <Divider />

          <Title level={2}>The lawful basis for processing your data</Title>
          <Paragraph>We process your data based on Article 6(a) of the GDPR, which states:</Paragraph>
          <blockquote>“(a) the data subject has given consent to processing his or her personal data for one or more specific purposes;”</blockquote>
          <Paragraph>
            If we use personal information for a purpose that requires your consent by provisions of law, we will always ask for your explicit agreement and record your consent.
          </Paragraph>

          <Divider />

          <Title level={2}>What Personal Data do we collect and how?</Title>
          <Paragraph>
            Our primary goal is to collect information to provide and improve our Services, to deliver your use of the Services, and enable you to enjoy and easily navigate our
            Services.
          </Paragraph>
          <ul>
            <li>
              <strong>Non-identifying information:</strong> This is collected as you navigate our application through cookies and other similar technologies, including the date and
              duration of your visit.
            </li>
            <li>
              <strong>Identifying information:</strong> This is collected when you interact with our services, such as creating an account or making a purchase. This includes your
              name, email address, etc.
            </li>
          </ul>

          <Divider />

          <Title level={2}>What do we do with your information?</Title>
          <ul>
            <li>To provide, support, and improve our Services.</li>
            <li>To provide customer support and answer questions.</li>
            <li>To analyze statistical data.</li>
            <li>To meet legal requirements such as court orders or lawful requests.</li>
          </ul>

          <Divider />

          <Title level={2}>How is your data protected?</Title>
          <Paragraph>
            We take the necessary technical and organizational measures to adequately protect the data of the Data Controller by meeting the requirements of Art. 32 of the GDPR. We
            use safe protocols for communication and transferring data (such as HTTPS). We use anonymizing, pseudonymization and encryption where suitable.
          </Paragraph>

          <Divider />

          <Title level={2}>How long do we retain your data?</Title>
          <Paragraph>
            The data processed by us is deleted or limited in its processing in accordance with Articles 17 and 18 of GDPR. Unless explicitly stated in this privacy policy, the
            data stored by us are deleted as soon as they are no longer required for their purpose, and the deletion does not conflict with any statutory storage requirements.
          </Paragraph>

          <Divider />

          <Title level={2}>Further Information</Title>
          <Paragraph>
            If you require any more information or have any questions about our privacy policy, please feel free to contact us by email at{" "}
            <a href="mailto:info@areax.ai">info@areax.ai</a>.
          </Paragraph>
        </Typography>
      </Card>
    </div>
  );
};

export default PrivacyPolicies;
